'use client';

import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { tv } from 'tailwind-variants';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Typography } from '@/components/ui/typography/typography.component';
import { Button } from '@/components/ui/button/button.component';
import { Card } from '@/components/ui/card.component';
import { MostSearchedResult } from '@/types/most-search-type';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';

const style = tv({
  slots: {
    buttonProcedure:
      'flex flex-row items-start justify-between gap-0 h-fit px-6 py-3 w-full xl:py-4 xl:pl-5 xl:pr-4',
    separator: 'w-full -mx-11',
  },
  variants: {
    isTop: {
      true: {
        buttonProcedure: 'xl:pt-0',
      },
    },
    isBottom: {
      true: {
        buttonProcedure: 'xl:pb-0',
      },
    },
    hideOnDesktop: {
      true: {
        buttonProcedure: 'xl:hidden',
        separator: 'xl:hidden',
      },
    },
  },
});

interface ProceduresCardProps {
  procedures?: MostSearchedResult[];
  onSeeMore?: () => void;
  hideSeeMoreOnDesktop?: boolean;
  goToSearchProcedureSchedules: (procedure: MostSearchedResult) => void;
}

export function ProceduresCard({
  procedures,
  onSeeMore,
  hideSeeMoreOnDesktop,
  goToSearchProcedureSchedules,
}: ProceduresCardProps) {
  const { buttonProcedure, separator } = style();

  return (
    <Card
      data-testid={DataTestIdEnum.CITY_MOST_SEARCHED_SECTION_CARD}
      className="flex flex-col justify-end items-center w-full px-0 py-0 rounded-lg xl:py-4 xl:rounded-xl h-fit"
    >
      {procedures?.map((procedure, index) => (
        <div
          key={`key-most-searched-procedures-${procedure.type}${procedure.id}`}
          className="flex flex-col justify-end items-center w-full"
        >
          <Button
            variant="text"
            data-testid={DataTestIdEnum.HOME_MOST_SEARCHED_ITEM}
            onClick={() => goToSearchProcedureSchedules(procedure)}
          >
            <div
              className={buttonProcedure({
                isTop: index === 0,
                isBottom: index === procedures.length - 1 && (!onSeeMore || hideSeeMoreOnDesktop),
              })}
            >
              <Typography className="text-green-500 justify-start text-nowrap truncate font-bold text-start line-clamp-2">
                {formatToTitleCase(procedure.displayName)}
              </Typography>
            </div>
          </Button>
          {index != procedures.length - 1 && <hr className={separator()} />}
        </div>
      ))}
      <>
        {onSeeMore && (
          <>
            {!!procedures?.length && <hr className={separator({ hideOnDesktop: hideSeeMoreOnDesktop })} />}
            <Button
              variant="text"
              className={buttonProcedure({
                className: 'justify-start font-bold',
                isBottom: true,
                isTop: Number(procedures?.length) === 0,
                hideOnDesktop: hideSeeMoreOnDesktop,
              })}
              startIconClassName="mt-[2px] mr-2"
              onClick={onSeeMore}
              startIcon={faArrowRight}
            >
              Ver mais
            </Button>
          </>
        )}
      </>
    </Card>
  );
}
